import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  IconButton,
  Button,
  makeStyles,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { appUrl } from "../../../../../utils/config";
import axios from "axios";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(2),
  },
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    width: "900px",
  },
  headerCell: {
    fontWeight: 600,
    color: "#333",
    backgroundColor: "#f0f0f0",
    padding: "10px 16px",
    textAlign: "left",
  },
  resetChangesButton: {
    border: "1px solid #000000",
    color: "#000000",
    fontWeight: 600,
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    marginTop: theme.spacing(2),
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  bodyCell: {
    padding: "10px 16px",
    textAlign: "left",
    verticalAlign: "middle",
  },
  textField: {
    "& input": {
      padding: "6px 8px",
      height: "30px",
    },
  },
  addButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  addIconCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "16px",
    fontWeight: 600,
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  switchBase: {
    "&.Mui-checked": {
      color: "#007bff",
    },
    "&.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#007bff",
    },
  },
  inlineActions: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "8px",
    gap: "4px",
  },
  iconBtn: {
    padding: "2px",
  },
  saveButton: {
    backgroundColor: "#f26522",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#d4551a",
    },
  },
}));

const ProductReleaseTestStatusTab = (props) => {
  const { featuresAccess, cachedData, onRefresh } = props;
  const classes = useStyles();
  const [error, setError] = useState(null);
  const [isUpdated, setIsUpdated] = useState(false);
  const [savingStates, setSavingStates] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const [productReleaseTestStatuses, setProductReleaseTestStatuses] = useState([]);
  
  const canEdit = featuresAccess?.editSponsorNetworkDetails ?? false;

  // Map raw API response to local row structure
  const mapFromSource = (sourceList = []) =>
    sourceList.map((item, index) => {
      const statusType = item.statusType || item.status_type || item.type || "";
      return {
        id: item.statusID || item.status_id || item.id || `ID-${index + 1}`,
        name: item.statusName || item.status_name || item.name || "",
        selected: !!item.activated,
        type: statusType === "new" || statusType === "New" ? "New" : statusType,
        isNew: false,
        lastUpdatedBy: item.lastUpdatedBy || item.last_updated_by,
        newStatusName: item.newStatusName || null,
      };
    });

  // Map data from API response - no API calls, no loaders when dropdown changes
  useEffect(() => {
    setError(null);
    // Map data from API response (apiData from parent)
    const apiData = props.apiData || {};
    if (apiData?.productRelease && apiData.productRelease.length > 0) {
      const list = apiData.productRelease || [];
      const formattedStatuses = mapFromSource(list);
      setProductReleaseTestStatuses(formattedStatuses);
    } else {
      setProductReleaseTestStatuses([]);
    }
  }, [props.apiData?.productRelease]);

  // Use parent's loading state (only shows when API is called)
  const isLoading = props.isLoading || false;

  const addProductReleaseTestStatus = async (index) => {
    const statusToAdd = productReleaseTestStatuses[index];
    if (!statusToAdd.name?.trim() || savingStates[index]) return;
    setSavingStates((prev) => ({ ...prev, [index]: true }));
    try {
      const payload = {
        sponsorId: 2,
        ecosystemId: 1,
        projectDevelopmentStatuses: [{
          statusType: "new",
          statusName: statusToAdd.name.trim(),
          activated: statusToAdd.selected,
          objectType: "ProductReleaseTest"
        }]
      };

      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/addPDStatuses`;
      const response = await axios.post(url, payload);

      if (response.data?.status === true || response.data?.status === "true") {
        // Update the status in local state to mark it as saved
        setProductReleaseTestStatuses((prev) =>
          prev.map((status, i) =>
            i === index
              ? { ...status, id: response.data?.data?.statusID || response.data?.data?.id || status.id, isNew: false, type: "New" }
              : status
          )
        );
        // Ask parent to refresh all statuses from the single API
        if (onRefresh) {
          onRefresh();
        } else {
          if (onRefresh) {
          onRefresh();
        }
        }
        if (props.setProductReleaseTestStatus) {
          props.setProductReleaseTestStatus((prev) => !prev);
        }
      } else {
        setError(response.data?.message || "Failed to add product release test status.");
      }
    } catch (error) {
      console.error("Error adding product release test status:", error);
      setError("An error occurred while adding the product release test status.");
    }
    setSavingStates((prev) => ({ ...prev, [index]: false }));
  };

  const handleSaveChanges = async () => {
    try {
      const isAnyStatusSelected = productReleaseTestStatuses.filter((status) => status.isUpdated);
      if (!isAnyStatusSelected.length) return;
      
      if (productReleaseTestStatuses.every((status) => !status.selected)) {
        setErrorMessage("Please enable at least one in toggle mode.");
        return;
      }
      setErrorMessage("");

      const payload = {
        sponsorId: 2,
        ecosystemId: 1,
        projectStatuses: [],
        campaignStatuses: [],
        changeControlStatuses: [],
        deviationStatuses: [],
        inProcessTestStatuses: [],
        productReleaseTestStatuses: isAnyStatusSelected.map((status) => ({
          statusID: status.id,
          statusName: status.name,
          statusType: status.type,
          activated: status.selected,
          markForDelete: false,
          newStatusName: status.newStatusName || null,
        })),
      };

      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updatePDStatuses`;
      const response = await axios.post(url, payload);

      if (response.data?.status === true || response.data?.status === "true") {
        if (onRefresh) {
          await onRefresh();
        } else {
          if (onRefresh) {
            onRefresh();
          }
        }
        setIsUpdated(false);
      } else {
        setErrorMessage(response.data?.message || "Failed to update product release test statuses.");
      }
    } catch (error) {
      console.error("Error updating product release test statuses:", error);
      setErrorMessage("An error occurred while updating product release test statuses. Please try again.");
    }
  };

  const handleDelete = async (index) => {
    try {
      const statusToDelete = productReleaseTestStatuses[index];
      if (statusToDelete.isNew) {
        setProductReleaseTestStatuses((prev) => prev.filter((_, i) => i !== index));
        return;
      }

      const payload = {
        sponsorId: 2,
        ecosystemId: 1,
        projectStatuses: [],
        campaignStatuses: [],
        changeControlStatuses: [],
        deviationStatuses: [],
        inProcessTestStatuses: [],
        productReleaseTestStatuses: [{
          statusID: statusToDelete.id,
          statusName: statusToDelete.name,
          statusType: statusToDelete.type,
          activated: statusToDelete.selected,
          markForDelete: true,
          newStatusName: statusToDelete.newStatusName || null,
        }],
      };

      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updatePDStatuses`;
      const response = await axios.post(url, payload);

      if (response.data?.status === true || response.data?.status === "true") {
        if (onRefresh) {
          await onRefresh();
        } else {
          if (onRefresh) {
            onRefresh();
          }
        }
        if (props.setProductReleaseTestStatus) {
          props.setProductReleaseTestStatus((prev) => !prev);
        }
      } else {
        setError(response.data?.message || "Failed to delete product release test status.");
      }
    } catch (error) {
      console.error("Error deleting product release test status:", error);
      setError("An error occurred while deleting the product release test status.");
    }
  };

  const handleToggle = (index) => {
    const updated = [...productReleaseTestStatuses];
    updated[index].selected = !updated[index].selected;
    updated[index].isUpdated = true;
    setProductReleaseTestStatuses(updated);
    setIsUpdated(true);
  };

  const handleNameChange = (index, value) => {
    const updated = [...productReleaseTestStatuses];
    updated[index].name = value;
    setProductReleaseTestStatuses(updated);
    setIsUpdated(true);
  };

  const handleResetChanges = () => {
    if (isUpdated) {
      if (onRefresh) {
        onRefresh();
      } else {
        if (onRefresh) {
          onRefresh();
        }
      }
      setIsUpdated(false);
      setErrorMessage("");
    }
  };

  const handleAddStatus = () => {
    setProductReleaseTestStatuses([
      ...productReleaseTestStatuses,
      {
        name: "",
        selected: false,
        isNew: true,
      },
    ]);
  };

  const handleCancel = (index) => {
    const statusToCancel = productReleaseTestStatuses[index];
    if (statusToCancel.isNew) {
      setProductReleaseTestStatuses((prev) => prev.filter((_, i) => i !== index));
      return;
    }
    setProductReleaseTestStatuses((prev) =>
      prev.map((status, i) =>
        i === index
          ? {
              ...status,
              isEditing: false,
              name: status.originalName || status.name,
            }
          : status
      )
    );
  };

  return (
    <div className={classes.container} style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <div style={{ display: "flex", gap: "40px", alignItems: "flex-start" }}>
        <Table className={`${classes.table} default-table-styles`}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.headerCell}>Selection</TableCell>
              <TableCell className={classes.headerCell}>Product Release Status</TableCell>
              <TableCell className={classes.headerCell} style={{ whiteSpace: "nowrap" }}>
                Product Release Status ID
              </TableCell>
              <TableCell className={classes.headerCell}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
                    <CircularProgress />
                  </div>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60px", backgroundColor: "#ffecec", color: "#b00020", border: "1px solid #ff9999" }}>
                    <Typography>{error}</Typography>
                  </div>
                </TableCell>
              </TableRow>
            ) : productReleaseTestStatuses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60px", color: "#666" }}>
                    <Typography>No Product Release Statuses Found</Typography>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              productReleaseTestStatuses.map((item, index) => (
                <TableRow key={item.id}>
                  <TableCell className={classes.bodyCell}>
                    <Switch
                      checked={item.selected}
                      onChange={() => handleToggle(index)}
                      classes={{ switchBase: classes.switchBase }}
                      disabled={!canEdit}
                    />
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {item.isNew ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <TextField
                          placeholder="Type Text Here"
                          value={item.name}
                          onChange={(e) => handleNameChange(index, e.target.value)}
                          className={classes.textField}
                          variant="outlined"
                          inputProps={{
                            onBeforeInput: (e) => {
                              const allowedChars = /^[a-zA-Z0-9\s\-()]+$/;
                              if (!allowedChars.test(e.data)) {
                                e.preventDefault();
                              }
                            }
                          }}
                        />
                        <div className={classes.inlineActions}>
                          <IconButton onClick={() => handleCancel(index)} className={classes.iconBtn}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => addProductReleaseTestStatus(index)}
                            className={classes.iconBtn}
                            disabled={!item.name?.trim() || productReleaseTestStatuses.some((s, i) => i !== index && s.name?.trim().toLowerCase() === item.name?.trim().toLowerCase()) || savingStates[index]}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                    ) : (
                      item.name
                    )}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>{item.id}</TableCell>
                  <TableCell className={classes.bodyCell}>
                    {canEdit && (item.isNew == true || item.type === "New") && (
                      <IconButton aria-label="delete" onClick={() => handleDelete(index)}>
                        <DeleteIcon className={classes.deleteIcon} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <div
          className={classes.buttonContainer}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "16px",
            marginTop: "3px",
            marginLeft: "0rem",
          }}
        >
          <Button
            className={classes.saveButton}
            type="button"
            onClick={handleSaveChanges}
            disabled={!isUpdated || !canEdit}
            style={{
              opacity: !isUpdated || !canEdit ? 0.7 : 1,
              color: "#fff",
            }}
          >
            Save Selection
          </Button>
          <Button
            className={classes.resetChangesButton}
            type="button"
            onClick={handleResetChanges}
            disabled={!isUpdated || !canEdit}
            style={{
              opacity: !isUpdated || !canEdit ? 0.8 : 1,
            }}
          >
            Reset Selection
          </Button>
        </div>
      </div>
      {errorMessage && (
        <div style={{ color: "red", marginTop: 10 }} className="error-message">
          {errorMessage}
        </div>
      )}
      {canEdit && (
        <Button variant="outlined" onClick={handleAddStatus} className={classes.addButton}>
          <span className={classes.addIconCircle}>+</span>
          Add New Product Release Status
        </Button>
      )}
    </div>
  );
};

export default ProductReleaseTestStatusTab;

